<template>
  <section class="main">
    <loader class="main__loader" v-if="!isLoaded"></loader>
    <template v-else>
      <div class="main__content main__content_a">
        <back-link
          v-if="isRepo"
          :to="`/repos/${repoId}`"
          class="back-link">{{ repoName }}</back-link>
        <div class="section heading">
          <div
            class="heading__icon"
            :style="`background-image: url(${app.image})`"
          ></div>
          <div
            ref="desc"
            class="heading__desc"
            :class="{
              heading__desc_expanded: isDescExpanded,
            }"
          >
            <div class="heading__mobile">
              <div
                class="heading__icon-mobile"
                :style="`background-image: url(${app.image})`"
              ></div>
              <h1 class="heading__title">
                <span class="heading__title-inner">{{ app.name }}</span
                ><span class="heading__badge" v-if="isTweaked">{{
                  $t("TWEAKED")
                }}</span>
              </h1>
            </div>
            <div ref="desc__inner">
              <html-with-links
                v-if="!isRepo"
                tag="p"
                class="heading__text"
                :html="app.description"
              ></html-with-links>
              <p v-else class="heading__text">{{ app.description }}</p>
            </div>
            <a href="#" class="more" @click.prevent="isDescExpanded = true">{{
              $t("VIEW MORE")
            }}</a>
          </div>
        </div>
        <div class="section" v-if="areScreenshotsVisible">
          <h2 class="h2">
            {{ $t("SCREENSHOTS") }}
            <ul class="select" v-if="!Array.isArray(app.screenshots)">
              <li
                v-for="(value, key) in details.screenshots"
                class="select__tab"
                :class="{ select__tab_active: screenshotTab === key }"
                @click="screenshotTab = key"
                :key="key"
              >
                {{ beautifyDevice(key) }}
              </li>
            </ul>
          </h2>
          <ul
            v-if="Array.isArray(app.screenshots)"
            class="screenshots"
          >
            <li
              class="screenshots__item"
              v-for="(screenshot, idx) in app.screenshots"
              :key="idx"
            >
              <img class="screenshots__img" :src="screenshot" />
            </li>
          </ul>
          <ul
            v-else
            class="screenshots"
            v-for="(array, key) in details.screenshots"
            :key="key"
            v-show="screenshotTab === key"
          >
            <li
              class="screenshots__item"
              v-for="screenshot in array"
              :key="screenshot.src"
            >
              <img class="screenshots__img" :src="screenshot.src" />
            </li>
          </ul>
        </div>
      </div>
      <sidebar
        :linkToInstallError="linkToInstallError"
        :copyrightedBy="app.copyrightedBy"
        :appStoreUrl="app.appStoreUrl"
        :isTweaked="isTweaked"
        :tweakedApps="tweakedApps"
        :type="type"
        :currentVersion="app.version"
        :versions="app.versions"
        :id="details.id"
        :originalId="details.original_trackid"
        :originalType="details.original_section"
        :repo="isRepo"
        class="main__sidebar"
      />
      <div class="main__content main__content_b main__content_ptop">
        <div v-if="app.whatsnew" class="section">
          <h2 class="h2">{{ $t("WHATSNEW") }}</h2>
          <div
            ref="changelog"
            class="changelog"
            :class="{ changelog_expanded: isChangelogExpanded }"
          >
            <p ref="changelog__inner" v-html="app.whatsnew"></p>
            <a
              href="#"
              class="more"
              @click.prevent="isChangelogExpanded = true"
              >{{ $t("VIEW MORE") }}</a
            >
          </div>
        </div>
        <div class="section section_noborder">
          <h2 class="h2 h2_badge">
            {{ $t("INFORMATION") }}
            <span
              class="info-badge"
              v-if="details.compatibility_string"
            >{{ details.compatibility_string }}</span>
          </h2>
          <div class="info">
            <ul class="info__list">
              <li class="info__item" v-if="app.developer.name">
                <span class="info__intro">{{ $t("DEVELOPER") }}:</span>
                <router-link
                  v-if="app.developer.id"
                  class="info__link"
                  :to="`/search/?type=${type}&dev=${app.developer.id}`"
                  >{{ app.developer.name }}</router-link
                >
                <span v-else>{{ app.developer.name }}</span>
              </li>
              <li class="info__item" v-if="app.website">
                <span class="info__intro">{{ $t("WEBSITE") }}:</span>
                <a class="info__link" :href="app.website" target="_blank">{{
                  $t("VISIT")
                }}</a>
              </li>
              <li
                class="info__item"
                v-if="genres[type] && genres[type][details.genre_id]"
              >
                <span class="info__intro">{{ $t("CATEGORY") }}:</span>
                <router-link
                  class="info__link"
                  :to="`/search/?type=${type}&genre=${details.genre_id}`"
                >
                  {{
                    $t((genres[type][details.genre_id].name || "").toUpperCase())
                  }}</router-link
                >
              </li>
              <li
                class="info__item"
                v-if="this.details.last_parse_itunes.customer_rating"
              >
                <span class="info__intro">{{ $t("CUSTOMER RATINGS") }}:</span>
                {{ this.details.last_parse_itunes.customer_rating }}
              </li>
              <li
                class="info__item"
                v-if="
                  app.updatedAt
                "
              >
                <span class="info__intro">{{ $t("UPDATED") }}:</span>
                {{ app.updatedAt }}
              </li>
              <li
                class="info__item"
                v-if="app.version"
              >
                <span class="info__intro">{{ $t("VERSION") }}:</span>
                {{ app.version }}
              </li>
              <li class="info__item" v-if="app.size">
                <span class="info__intro">{{ $t("SIZE") }}:</span>
                {{ app.size }}
              </li>
              <li
                class="info__item"
                v-if="this.details.last_parse_itunes.languages"
              >
                <span class="info__intro">{{ $t("LANGUAGE") }}:</span>
                {{ this.details.last_parse_itunes.languages }}
              </li>
              <li class="info__item" v-if="this.details.last_parse_itunes.seller">
                <span class="info__intro">{{ $t("SELLER") }}:</span>
                {{ this.details.last_parse_itunes.seller }}
              </li>
              <li
                class="info__item"
                v-if="this.details.last_parse_itunes.publisher"
              >
                <span class="info__intro">{{ $t("PUBLISHER") }}:</span>
                {{ this.details.last_parse_itunes.publisher }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import prettysize from 'prettysize';
import { sanitizeUrl } from '@braintree/sanitize-url';
import Sidebar from '@/components/AppSidebar/index.vue';
import Loader from '@/components/Loader';
import HtmlWithLinks from '@/components/HtmlWithLinks';
import BackLink from '@/components/BackLink';
import { mapGetters } from 'vuex';
import axios from 'axios';

export default {
  components: {
    Sidebar, Loader, HtmlWithLinks, BackLink,
  },
  data() {
    return {
      requestCounter: 0,
      isDescExpanded: false,
      screenshotTab: 'iphone',
      isChangelogExpanded: false,
      details: {
        image: '',
        last_parse_itunes: {
          artist: {},
          raw_parse_itunes: {},
        },
      },
      repoName: '',
      appFromRepo: {},
      versions: {},
      isLoaded: false,
      prevRoute: null,
      linkToInstallError: false,
    };
  },
  computed: {
    ...mapGetters(['isDeviceLinked']),
    title() {
      return this.app.name;
    },
    repoId() {
      return this.$route.params.repoId;
    },
    isRepo() {
      return Boolean(this.repoId);
    },
    id() {
      return this.$route.params.id;
    },
    isTweaked() {
      return Boolean(this.details.original_trackid) && this.details.original_trackid !== '0';
    },
    tweakedApps() {
      const versions = this.details.tweaked_versions;
      if (!versions) {
        return [];
      }

      const apps = [];
      Object.keys(versions).forEach((type) => {
        apps.push(...versions[type].map(app => ({ type, id: app.trackid, name: app.name })));
      });

      return apps;
    },
    genres() {
      return this.$store.getters.genres;
    },
    type() {
      if (this.isRepo) {
        return 'repo';
      }

      return this.$route.params.type;
    },
    image() {
      let image = '';

      if (this.isRepo) {
        return this.appFromRepo.iconURL;
      }

      try {
        image = this.details.last_parse_itunes.raw_parse_itunes.artworkUrl512;
      } catch {
        // eslint-disable-next-line prefer-destructuring
        image = this.details.image;
      }
      return image || this.details.image;
    },
    areScreenshotsVisible() {
      if (!this.app.screenshots) {
        return false;
      }

      if (Array.isArray(this.app.screenshots)) {
        return this.app.screenshots.length;
      }

      if (typeof this.app.screenshots === 'object') {
        return Object.keys(this.app.screenshots).length;
      }

      return false;
    },
    githubLink() {
      if (!this.isRepo) {
        return '';
      }

      const owner = this.appFromRepo.githubOwner;
      const repository = this.appFromRepo.githubRepository;

      if (!owner || !repository) {
        return '';
      }

      return sanitizeUrl(`https://github.com/${owner}/${repository}`);
    },
    app() {
      if (this.isRepo) {
        const app = {
          name: this.appFromRepo.name,
          image: this.appFromRepo.iconURL,
          description: this.appFromRepo.localizedDescription,
          screenshots: this.appFromRepo.screenshotURLs,
          bundle_id: this.appFromRepo.bundleIdentifier,
          developer: {
            name: this.appFromRepo.developerName,
          },
          version: this.appFromRepo.version,
          updatedAt: (this.appFromRepo.versionDate
            ? new Date(this.appFromRepo.versionDate).toLocaleDateString() : null),
          size: (this.appFromRepo.size ? prettysize(this.appFromRepo.size) : null),
          website: this.githubLink,
          versions: {},
          appStoreUrl: null,
          copyrightedBy: null,
        };

        if (this.appFromRepo.downloadURL) {
          app.versions[this.appFromRepo.version] = [
            {
              name: this.appFromRepo.name,
              id: this.appFromRepo.bundleIdentifier,
              link: this.appFromRepo.downloadURL,
              host: this.getHost(this.appFromRepo.downloadURL),
              image: this.appFromRepo.iconURL,
              version: this.appFromRepo.version,
            },
          ];
        } else if (this.appFromRepo.versions) {
          this.appFromRepo.versions.forEach((versionData) => {
            if (!app.updatedAt) {
              app.updatedAt = new Date(versionData.date).toLocaleDateString();
            }
            if (!app.size) {
              app.size = prettysize(versionData.size);
            }

            app.versions[versionData.version] = [
              {
                name: this.appFromRepo.name,
                id: this.appFromRepo.bundleIdentifier,
                link: versionData.downloadURL,
                host: this.getHost(versionData.downloadURL),
                image: this.appFromRepo.iconURL,
                version: versionData.version,
              },
            ];
          });
        }

        return app;
      }

      return {
        name: this.details.name,
        image: this.image,
        description: this.details.description,
        screenshots: this.details.screenshots,
        whatsnew: this.details.whatsnew,
        copyrightedBy: this.details.copyrighted_by,
        developer: {
          name: this.details.pname,
          id: +this.details.artist_id,
        },
        version: this.details.version,
        updatedAt: this.details.last_parse_itunes.published,
        size: this.details.last_parse_itunes.size,
        website: this.details.pwebsite,
        appStoreUrl: this.details.appstore_url,
        versions: this.versions,
      };
    },
  },
  methods: {
    beautifyDevice(device) {
      const dict = {
        iphone: 'iPhone',
        ipad: 'iPad',
        appletv: 'Apple TV',
      };

      return dict[device] || device;
    },
    getHost(link) {
      try {
        const url = new URL(link);
        return url.host || url.hostname;
      } catch {
        return link;
      }
    },
    async handleTriggers() {
      await this.$nextTick();

      const descWrapper = this.$refs.desc && this.$refs.desc.getBoundingClientRect().bottom;
      const descText = this.$refs.desc__inner
        && this.$refs.desc__inner.getBoundingClientRect().bottom;

      if (descText <= descWrapper) {
        this.isDescExpanded = true;
      }

      if (this.$refs.changelog__inner
        && this.$refs.changelog__inner.getBoundingClientRect().height <= 85) {
        this.isChangelogExpanded = true;
      }
    },
    handle18PlusContent(data) {
      // eslint-disable-next-line camelcase
      if (data?.is_18plus !== 1) {
        return;
      }

      this.$confirm(this.$t('AGE_RESTRICTED_CONTENT_ALERT'), {
        hidePage: true,
        closeManually: true,
        okText: 'GO BACK',
        cancelText: 'IM_18_PLUS',
      }).then(() => {
        this.$router.push(this.prevRoute?.fullPath || '/');
      }).finally(() => {
        this.$modal.hide('dialog');
      });
    },
    goTo404() {
      this.$router.replace({
        name: 'error',
        params: {
          message: '404_CONTENT_MESSAGE',
          link: '/',
          linkText: 'GO_TO_MAIN_PAGE',
        },
      });
    },
    goToRepo() {
      this.$router.replace({
        name: 'error',
        params: {
          message: 'NO_APP_IN_REPO_ERROR',
          link: '/my/repos',
          linkText: 'MY_REPOSITORIES',
        },
      });
    },
    async getInfo() {
      this.isLoaded = false;

      if (this.isRepo) {
        try {
          const response = await this.$api.getRepo(this.repoId);
          if (!response.success) {
            throw response;
          }

          this.repoName = response.data[0].name;

          const contentsResponse = await axios.get(response.data[0].contents_uri);
          const app = contentsResponse.data.apps[this.id];


          if (!app) {
            this.goToRepo();
            return;
          }

          this.appFromRepo = app;
        } catch (response) {
          this.$router.replace({
            name: 'error',
            params: {
              message: response.errors[0].translated,
              link: '/my/repos',
              linkText: 'MY_REPOSITORIES',
            },
          });
        } finally {
          this.isLoaded = true;
        }

        return;
      }

      const data = await this.$api.getAppDetails({
        type: this.type,
        trackid: this.id,
      });

      if (!data.length) {
        this.goTo404();
        return;
      }

      this.details = { ...data[0] };

      this.handle18PlusContent(this.details);

      try {
        this.details.screenshots = JSON.parse(this.details.screenshots);
        // eslint-disable-next-line
        this.screenshotTab = Object.keys(this.details.screenshots)[0];
      } catch {
        this.details.screenshots = null;
      }

      try {
        this.details.last_parse_itunes = JSON.parse(this.details.last_parse_itunes) || {
          artist: {},
          raw_parse_itunes: {},
        };
      } catch {
        this.details.last_parse_itunes = {
          artist: {},
          raw_parse_itunes: {},
        };
      }

      try {
        const response = await this.$api.getLinks({
          type: this.type,
          trackid: this.id,
        });

        this.versions = (response.data && response.data[this.id]) || {};

        this.linkToInstallError = response.errors.some(error => error.code === 'ERROR_NO_DEVICE_LINKED');
      } finally {
        this.isLoaded = true;
      }
    },
  },
  watch: {
    isLoaded(newVal) {
      if (newVal) {
        this.handleTriggers();
      }
    },
    // eslint-disable-next-line
    '$route.params.id': function () {
      this.getInfo();
    },
  },
  serverPrefetch() {
    return this.getInfo();
  },
  created() {
    this.getInfo();
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // eslint-disable-next-line no-param-reassign
      vm.prevRoute = from;
    });
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

@mixin gradient {
  &:before {
    content: "";
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 50px;
    background-image: var(--app-fadeout-gradient);
    pointer-events: none;
  }

  &_expanded {
    max-height: unset;

    &:before,
    .more {
      display: none;
    }
  }
}

.changelog,
.heading__text {
  &::v-deep {
    img {
      max-width: 100%;
      height: auto;
    }

    p {
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    }

    strong {
      font-weight: 700;
    }

    ul,
    ol {
      &:not(:last-child) {
        margin-bottom: 7px;
      }
    }

    ol {
      list-style: decimal inside;
    }

    ul {
      list-style: disc inside;
    }

    li {
      margin-bottom: 3px;
    }
  }
}

.main {
  display: grid;
  grid-template-areas:
    "loader loader"
    "content-a sidebar"
    "content-b sidebar"
    "none sidebar";
  grid-template-columns: minmax(0, 1fr)  370px;
  column-gap: 50px;

  @include notebook {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "loader"
      "content-a"
      "sidebar"
      "content-b";
  }

  &__loader {
    grid-area: loader;
  }

  &__content {
    &_a {
      grid-area: content-a;
    }

    &_b {
      grid-area: content-b;
    }

    &_ptop {
      padding-top: 30px;

      @include notebook {
        padding-top: 10px;
      }
    }

    @include notebook {
      max-width: 100%;
    }
  }

  &__sidebar {
    grid-area: sidebar;

    @include notebook {
      width: 100%;
    }
  }
}

.section {
  padding-bottom: 30px;
  border-bottom: 1px solid #e5e5e5;

  @include notebook {
    padding-bottom: 10px;
  }

  &_noborder {
    border: none;
  }

  &:not(:first-child) {
    padding-top: 30px;

    @include notebook {
      padding-top: 10px;
    }
  }
}

.heading {
  display: flex;

  &__desc {
    max-height: 227px;
    overflow: hidden;
    position: relative;

    &_book {
      max-height: 340px;
    }

    @include gradient;
  }

  &__mobile {
    @include notebook {
      display: flex;
    }
  }

  &__icon {
    flex: 0 0 227px;
    height: 227px;
    margin-right: 27px;
    border-radius: 40px;

    &:not(&_vertical) {
      border-radius: 22.5%;
      mask: url(../assets/ios-mask.svg) center/100% 100% no-repeat;
    }

    @include notebook {
      display: none;
    }

    &_vertical {
      height: 340px;
    }

    &,
    &-mobile {
      background-size: cover;
      background-position: center;
    }

    &-mobile {
      display: none;

      @include notebook {
        display: block;
        width: 64px;
        flex: 0 0 64px;
        height: 64px;
        border-radius: 12px;
        margin-right: 10px;
      }

      &_vertical {
        height: 96px;
      }
    }
  }

  &__title {
    display: flex;
    align-items: center;
    color: var(--text-tertiary);
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 10px;
    min-width: 0;

    @include notebook {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      margin-bottom: 0;
    }

    &-inner {
      @include notebook {
        width: 100%;
      }
    }
  }

  &__badge {
    padding: 5px;
    color: #2b2b05;
    text-transform: uppercase;
    font-size: 14px;
    border-radius: 2px;
    background-color: var(--bg-warning);
    margin-left: 11px;

    @include notebook {
      margin-left: 0;
      margin-top: 5px;
    }
  }

  &__text {
    line-height: 16px;

    @include notebook {
      margin-top: 15px;
    }
  }

  &__separator {
    content: "";
    display: block;
    margin-top: 10px;
  }
}

.more {
  display: flex;
  align-items: center;
  color: $primary;
  font-weight: 500;
  position: absolute;
  left: 0;
  bottom: 0;

  @include notebook {
    right: 0;
    justify-content: center;
  }

  &:after {
    content: "";
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 4px 4px 0 4px;
    border-color: $primary transparent transparent transparent;
    margin-left: 3px;
    margin-top: 2px;
  }
}

.h2 {
  color: var(--text-tertiary);
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  @include notebook {
    margin-bottom: 10px;
  }

  &_badge {
    @include notebook {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}

.select {
  display: flex;
  margin-left: 20px;
  border-radius: 2px;

  @include notebook {
    margin-left: 10px;
  }

  &__tab {
    position: relative;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 400;
    flex-grow: 1;
    padding: 3px 25px;
    border: 1px solid rgba(96, 112, 128, 0.4);
    cursor: pointer;

    &:first-child {
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }

    &:last-child {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }

    &:before,
    &:after {
      content: "";
      display: none;
      position: absolute;
      top: -1px;
      bottom: -1px;
      width: 1px;
      background-color: rgba(96, 112, 128, 0.4);
    }

    &_active {
      background-color: var(--lighter-primary);
      border-color: lighten($primary, 30%);
      z-index: 1;

      &:before,
      &:after {
        background-color: lighten($primary, 30%);
      }
    }

    &:not(:first-child) {
      border-left: none;

      &:before {
        display: block;
        left: -1px;
      }
    }

    &:not(:last-child) {
      border-right: none;

      &:after {
        display: block;
        right: 0;
      }
    }
  }
}

.screenshots {
  display: flex;
  overflow: scroll;
  padding-bottom: 15px;

  &__item {
    &:not(:last-child) {
      margin-right: 5px;
    }
  }

  &__img {
    height: 350px;
  }
}

.changelog {
  display: inline-block;
  line-height: 16px;
  max-height: 85px;
  overflow: hidden;
  position: relative;
  @include gradient;
}

.ul {
  list-style: disc inside;
  margin-top: 5px;
}

.info-badge {
  background: var(--lighter-primary);
  border: 1px solid lighten($primary, 45%);
  border-radius: 2px;
  padding: 4px 8px;
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);
  margin-left: 10px;

  @include notebook {
    margin-left: 0;
    margin-top: 5px;
  }
}

.info {
  display: flex;

  @include notebook {
    flex-direction: column;
  }

  &__item {
    margin-bottom: 5px;
    line-height: 20px;
  }

  &__link {
    color: $primary;
    font-weight: 500;
  }

  &__intro {
    color: #525d66;
    margin-right: 15px;
  }

  &__downloads {
    margin-left: auto;

    @include notebook {
      margin-left: 0;
      margin-top: 10px;
    }

    &-heading {
      color: var(--text-tertiary);
      font-weight: 600;
      font-size: 18px;
      margin-bottom: 5px;
    }

    &-total {
      font-weight: 500;
      line-height: 20px;
    }
  }

  &__download {
    margin-bottom: 5px;
    line-height: 20px;
  }
}

.back-link {
  @include notebook {
    margin-bottom: 10px;
  }
}
</style>
